import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {
    MvsWidgetFormGenericEventConstants
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {
    CpProvisionCalculatedAgentResetService
} from '../../../service/api/cp-provision-calculated-agent-reset.service';
import {
    ProvisionCalculatedAgentResetPreviewDto,
    ProvisionCalculatedAgentResetRequestDto,
    ProvisionCalculatedAgentResetResultDto,
    ProvisionCalculatedAgentRowDto,
    ProvisionCalculatedAgentRuleDto
} from '../../../model/dto/api/provision-calculated-agent-reset.dto';
import {CpFeatureRight} from '../../../service/domain/cp-feature-right';

/** Load state of the mandatory preview — the dialog renders one branch per value. */
type CpResetPreviewState = 'idle' | 'loading' | 'loaded' | 'failed';

/**
 * Action dialog "Berechnete Vermittler neu ermitteln" on ONE `cp.ProvisionContract` event row
 * (requirement §3.6, FD-7, decision D-5).
 *
 *   GET  /mvsa/cp/provisionContracts/{id}/resetCalculatedAgents/preview
 *   POST /mvsa/cp/provisionContracts/{id}/resetCalculatedAgents   Body: { reason }
 *
 * ── WHAT THE ACTION DOES, AND WHAT IT DELIBERATELY DOES NOT DO
 *
 * It clears `calculatedAgentsResolved` on this one event row and nothing else. `agentsResolved` is
 * not touched, nothing is booked, and — the point an operator must not misread — NO EXISTING
 * calculated `ProvisionContractAgent` row is deleted, cancelled or corrected. The act is PURELY
 * ADDITIVE (signed as D-7/AB-8): the next calculation run adds only genuinely new rows and skips
 * every `(component, agent)` pair that already exists.
 *
 * That is why the preview carries THREE lists and the dialog renders all three:
 *   * {@link ProvisionCalculatedAgentResetPreviewDto.wouldFire}    — what would be ADDED;
 *   * {@link ProvisionCalculatedAgentResetPreviewDto.obsoleteRows} — rows the CURRENT configuration
 *     would no longer produce. DISPLAY ONLY. The dialog labels them as explicitly NOT removed and,
 *     when any exist, forces an extra confirmation naming that fact, so nobody presses the button
 *     expecting a clean-up. Removal stays the existing separate operator path (amount overwrite to
 *     0,00 / the cancellation route) and is deliberately absent from this dialog — a parallel
 *     cancel action in the same preview was explicitly rejected (AB-8);
 *   * {@link ProvisionCalculatedAgentResetPreviewDto.blockedBy}    — the German refusal reasons.
 *     When it is non-empty the confirm button is UNAVAILABLE, not merely failing on submit.
 *
 * ── REFUSAL CHANNEL (differs from the sibling changeAgent dialog)
 *
 * A blocked reset is NOT an HTTP 200 with `success = false`. The three blocking conditions — an
 * ACTIVE booking on the event row, its ProvisionRun working (IN_CALCULATION / IN_ACTIVATION /
 * IN_PAYOUT), or the row being cancelled — are raised as `ProvisionCheckRefusedException` and
 * rendered by `CpControllerExceptionAdvice` as HTTP 400 with `{success: false, data: "<German
 * text>"}`. {@link readRefusalText} lifts that sentence out of the error body and shows it verbatim
 * rather than a generic technical message. The preview is the way to see the block BEFORE pressing.
 *
 * ── FORM MECHANISM: TRANSIENT FORM WIDGET (no hand-written Angular form)
 *
 * The mandatory reason is captured by a transient form widget built from an `MvsFormDto` in the TS,
 * IDENTICALLY to {@link CpProvisionAgentChangeDialogComponent} and the two other action dialogs of
 * this module:
 *
 *   const form = new MvsFormDto();  form.formFields = { … MvsFormFieldDto … };
 *   WidgetFactory.createWidget(alias, name, 'form', 'transient', 'transient', objectType,
 *                              WidgetDataParam.create('form', form));
 *
 * `uiStringMaxLength = 255` mirrors the backend limit (`REASON_MAX_LENGTH`) and is turned into an
 * Angular `Validators.maxLength` by `MvsFormControlService`, so an over-long reason leaves the form
 * INVALID and the submit button locked — the client-side half of the same rule the server enforces.
 * The template holds NO form HTML: only the dialog frame, the preview, and the buttons.
 *
 * `MvsUiObjectService.openObjectViaDialog(...)` / `mvs-crud-object` are wrong here for the same
 * reason as in the sibling dialog: they write through the generic CRUD, whereas
 * `calculatedAgentsResolved` may only be cleared through this endpoint, and `reason` exists in no
 * table at all.
 *
 * ── PERMISSION (um right `cp_agentChange`, AX-08 — no new right)
 *
 * Both endpoints carry `cp_agentChange` PLUS the object right on `cp.ProvisionContract` (read for
 * the preview, write for the action); the preview is guarded as tightly as the action on purpose.
 * {@link canAgentChange} follows the module-wide optimistic pattern documented once in
 * {@link CpFeatureRight}: it starts GRANTED, a server 403 demotes it stickily and emits
 * {@link onForbidden} so the host locks the entry button that led here. A host that knows the grant
 * may bind `false` and the action is then hidden by that host before this dialog ever opens.
 *
 * ── HOSTS
 *
 * Two, both of which pass one ProvisionContract id: the CR-033 findings list of the configuration
 * check (`CpConfigCheckPanelComponent` — the worklist FD-7 names) and the "Ereignisse" tab of the
 * provision-run detail (`CpProvisionRunDetailComponent`).
 *
 * German UI text with real umlauts, English code and comments (module convention).
 */
@Component({
    selector: 'cp-provision-calculated-agent-reset-dialog',
    templateUrl: './cp-provision-calculated-agent-reset-dialog.component.html',
    styleUrls: ['./cp-provision-calculated-agent-reset-dialog.component.scss'],
    standalone: false
})
export class CpProvisionCalculatedAgentResetDialogComponent implements OnInit, OnChanges, OnDestroy {

    /** Maximum length of the reason — mirrors the backend `REASON_MAX_LENGTH` (§3.6 f). */
    static readonly REASON_MAX_LENGTH: number = 255;

    busy: boolean = false;
    initialized: boolean = false;

    /** The event row (`cp.ProvisionContract`, R-07) to release. */
    @Input() provisionContractId: number;

    /** Display text of the event row; purely informational, falls back to the id. */
    @Input() provisionContractLabel: string;

    /** um right `cp_agentChange` — optimistic default, demoted on 403; see the class TSDoc. */
    @Input() canAgentChange: boolean = true;

    /** Visibility; intended as a two-way binding (`[(visible)]`). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Fires after a successfully executed reset (including the idempotent no-op). */
    @Output() onReset = new EventEmitter<ProvisionCalculatedAgentResetResultDto>();

    /** Fires when the server refused the right with a 403 — the demotion signal (see class TSDoc). */
    @Output() onForbidden = new EventEmitter<void>();

    /** The transient capture widget for the mandatory reason (see class TSDoc). */
    formWidget: WidgetData;

    /** Last VALID form value; null as long as the form is incomplete or the reason is too long. */
    formValue: any = null;

    previewState: CpResetPreviewState = 'idle';

    /** The loaded preview, or null while none has arrived. */
    preview: ProvisionCalculatedAgentResetPreviewDto = null;

    /** German error text of a failed preview or a refused write, rendered inline. */
    failureMessage: string = null;

    /** Exposed for the template so the character limit is named where the user reads it. */
    protected readonly cReasonMaxLength: number =
        CpProvisionCalculatedAgentResetDialogComponent.REASON_MAX_LENGTH;

    private previewSubscription: Subscription;
    private resetSubscription: Subscription;

    constructor(
        protected messageService: MvsMessageService,
        protected mvsObjectService: MvsObjectService,
        protected confirmationService: ConfirmationService,
        protected resetService: CpProvisionCalculatedAgentResetService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['visible'] && this.visible) {
            this.refreshComponent();
        }
        if (changes['provisionContractId'] && this.visible) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.previewSubscription?.unsubscribe();
        this.resetSubscription?.unsubscribe();
    }

    initComponent(): void {
        this.initialized = true;
        if (this.visible) {
            this.refreshComponent();
        }
    }

    /**
     * Rebuilds form and preview fresh on every open. Both hosts open this dialog out of a LIST
     * whose selected row changes between openings, so a preview left over from the previously
     * viewed event row would be shown against the wrong id — the one mistake this dialog must not
     * make, because the preview is what the operator judges the write by.
     */
    refreshComponent(): void {

        this.previewSubscription?.unsubscribe();
        this.resetSubscription?.unsubscribe();

        this.formValue = null;
        this.failureMessage = null;
        this.preview = null;
        this.previewState = 'idle';
        this.busy = false;

        this.buildFormWidget();

        if (!this.provisionContractId) {
            return;
        }

        this.loadPreview();
    }

    /**
     * Builds the `MvsFormDto` of the reset. One field only — the reason — whose id matches
     * {@link ProvisionCalculatedAgentResetRequestDto} 1:1, so {@link executeReset} needs no
     * renaming logic.
     */
    private buildFormWidget(): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        // Reason — MANDATORY (§3.6 f); logged as INFO M-046 with the acting user and written into
        // the Envers revision of the event row.
        const reasonField = MvsFormFieldDto.createBasic('reason', 'textarea', 'Grund');
        reasonField.uiMandatory = true;
        reasonField.uiStringMaxLength = CpProvisionCalculatedAgentResetDialogComponent.REASON_MAX_LENGTH;
        reasonField.uiDescription =
            'Pflichtangabe (max. ' + CpProvisionCalculatedAgentResetDialogComponent.REASON_MAX_LENGTH
            + ' Zeichen): warum soll diese Ereigniszeile erneut auf berechnete Vermittler geprüft '
            + 'werden? Der Grund wird mit Ihrem Benutzer im Laufprotokoll und in der Historie '
            + 'festgehalten.';
        reasonField.order = 10;
        formFields['reason'] = reasonField;

        form.formFields = formFields;

        /**
         * AI: Transient form widget capturing the mandatory reason of a calculated-agent reset.
         *     Source: MvsFormDto built in the frontend (reason exists in no table) — the write
         *     goes to POST /cp/provisionContracts/{id}/resetCalculatedAgents.
         *     Operators use it to release ONE commission event row for a fresh derivation of its
         *     calculated agents; the act only adds rows, it never removes any.
         */
        this.formWidget = WidgetFactory.createWidget(
            'cp.contract.calculatedAgentReset.form',
            'Berechnete Vermittler neu ermitteln',
            'form',
            'transient',
            'transient',
            'cp.ProvisionContract',
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('form', form)
        );
    }

    /**
     * Mandatory preview (§3.6 c) — read-only, writes nothing.
     *
     * It is loaded on open and independent of the reason: the operator must be able to read the
     * consequences BEFORE typing anything, otherwise the preview arrives after the decision it is
     * supposed to inform. If it fails, {@link previewState} stays `failed` and the submit button
     * stays locked — without having seen `blockedBy` and the obsolete rows, the reset must not be
     * submitted.
     */
    private loadPreview(): void {

        this.previewState = 'loading';

        this.previewSubscription = this.resetService.previewReset(this.provisionContractId).subscribe({
            next: (preview: ProvisionCalculatedAgentResetPreviewDto) => {
                this.preview = preview;
                this.previewState = 'loaded';
            },
            error: (error) => {
                this.preview = null;
                this.previewState = 'failed';
                this.demoteOnForbidden(error);
                this.failureMessage = CpFeatureRight.isForbidden(error)
                    ? CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE)
                    : (this.readRefusalText(error)
                        ?? 'Die Vorschau konnte nicht geladen werden. Ohne Vorschau kann die '
                        + 'Neuermittlung nicht freigegeben werden.');
            }
        });
    }

    /**
     * Receives the captured reason. {@link formValue} is set ONLY while the form is fully valid,
     * which is where the max-length rule takes effect: an over-long reason leaves the group INVALID
     * and therefore leaves the submit button locked.
     */
    handleGenericEvent(event: WidgetGenericEvent): void {

        if (event.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return;
        }

        const formGroup: MvsFormGroup =
            event.object[MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP];

        this.formValue = formGroup.status === 'VALID' ? formGroup.value : null;
        this.failureMessage = null;
    }

    /** The rules the next run would apply — what the reset would ADD. */
    get wouldFire(): ProvisionCalculatedAgentRuleDto[] {
        return this.preview?.wouldFire ?? [];
    }

    /** Existing calculated rows the current configuration no longer produces — DISPLAY ONLY. */
    get obsoleteRows(): ProvisionCalculatedAgentRowDto[] {
        return this.preview?.obsoleteRows ?? [];
    }

    /** The German refusal reasons of the preview; empty when the reset is accepted. */
    get blockedBy(): string[] {
        return this.preview?.blockedBy ?? [];
    }

    /** True when the server refused the reset for this row — the button must then be unavailable. */
    get blocked(): boolean {
        return this.previewState === 'loaded' && this.preview?.resettable !== true;
    }

    /**
     * True when the row is ALREADY released. The reset then still runs and is accepted, but it
     * changes nothing (`released = false`, §3.6 e) — worth saying, because the operator would
     * otherwise read the unchanged result as a failure.
     */
    get alreadyReleased(): boolean {
        return this.previewState === 'loaded' && this.preview?.calculatedAgentsResolved === false;
    }

    /**
     * The submit button is active only when ALL of these hold: right granted, preview loaded for
     * exactly this row, the preview did not refuse, the reason captured and valid, nothing in
     * flight.
     */
    get submittable(): boolean {
        return this.canAgentChange
            && this.previewState === 'loaded'
            && !this.blocked
            && !!this.formValue
            && !this.busy;
    }

    /**
     * Why the submit button is locked, in German — one reason per cause, in the order the operator
     * meets them. Naming the right when the right is not the problem is exactly the misleading text
     * the optimistic pattern exists to avoid (see {@link CpFeatureRight}).
     */
    get submitTooltip(): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (this.previewState === 'loading') {
            return 'Die Vorschau wird geladen.';
        }

        if (this.previewState !== 'loaded') {
            return 'Die Vorschau muss geladen sein, bevor die Neuermittlung freigegeben werden kann.';
        }

        if (this.blocked) {
            return 'Diese Ereigniszeile kann derzeit nicht freigegeben werden — siehe die Gründe oben.';
        }

        if (!this.formValue) {
            return 'Bitte einen Grund angeben (max. ' + this.cReasonMaxLength + ' Zeichen).';
        }

        return 'Neuermittlung freigeben.';
    }

    /**
     * Submits.
     *
     * When obsolete rows exist, an EXPLICIT confirmation naming the additive-only semantics runs
     * first. This is the one moment an operator could plausibly expect the reset to clean those
     * rows up, so the sentence they must accept says in so many words that it will not — see the
     * class TSDoc and D-7/AB-8. Without obsolete rows there is nothing to misread and the preview
     * itself is the confirmation.
     */
    handleReset(): void {

        if (!this.submittable) {
            return;
        }

        if (!this.obsoleteRows.length) {
            this.executeReset();
            return;
        }

        // DOCUMENTED DEVIATION from UX7 (Font Awesome only): the `icon` of
        // ConfirmationService.confirm() renders through PrimeNG's own ConfirmDialog template, which
        // expects a `pi` class there — the same deviation the sibling dialog documents. No other
        // icon in this component uses PrimeIcons.
        this.confirmationService.confirm({
            header: 'Obsolete berechnete Sätze bleiben bestehen',
            message: this.obsoleteRows.length === 1
                ? 'Ein bestehender berechneter Satz wird von der aktuellen Konfiguration nicht mehr '
                + 'erzeugt. Die Freigabe ergänzt nur — dieser Satz wird NICHT entfernt und NICHT '
                + 'storniert. Das Bereinigen bleibt der bestehende, separate Weg (Betrag auf 0,00 '
                + 'überschreiben bzw. Storno). Freigabe trotzdem durchführen?'
                : this.obsoleteRows.length + ' bestehende berechnete Sätze werden von der aktuellen '
                + 'Konfiguration nicht mehr erzeugt. Die Freigabe ergänzt nur — diese Sätze werden '
                + 'NICHT entfernt und NICHT storniert. Das Bereinigen bleibt der bestehende, '
                + 'separate Weg (Betrag auf 0,00 überschreiben bzw. Storno). Freigabe trotzdem '
                + 'durchführen?',
            icon: 'pi pi-info-circle',
            acceptLabel: 'Freigabe durchführen',
            rejectLabel: 'Abbrechen',
            accept: () => this.executeReset()
        });
    }

    /**
     * Writes the release.
     *
     * A refusal never arrives in the success branch: it is an HTTP 400 carrying the German sentence
     * (see the class TSDoc), so it is handled in the error branch and shown verbatim. A success
     * with `released = false` is the idempotent no-op and is reported as such rather than as a
     * change that did not happen.
     */
    private executeReset(): void {

        const request: ProvisionCalculatedAgentResetRequestDto = {
            reason: this.formValue['reason']
        };

        this.busy = true;
        this.failureMessage = null;

        this.resetSubscription = this.resetService.reset(this.provisionContractId, request).subscribe({
            next: (result: ProvisionCalculatedAgentResetResultDto) => {
                this.busy = false;

                if (result?.released === false) {
                    this.messageService.showInfoMessage(
                        'Bereits freigegeben',
                        'Diese Ereigniszeile war für berechnete Vermittler bereits freigegeben — '
                        + 'es hat sich nichts geändert.');
                } else {
                    this.messageService.showSuccessMessage(
                        'Neuermittlung freigegeben',
                        'Der nächste Berechnungslauf ergänzt die fehlenden berechneten Vermittler. '
                        + 'Bestehende Sätze bleiben unverändert.');
                }

                this.informObjectChanged();
                this.onReset.emit(result);
                this.handleClose();
            },
            error: (error) => {
                this.busy = false;
                this.demoteOnForbidden(error);
                this.failureMessage = CpFeatureRight.isForbidden(error)
                    ? CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE)
                    : (this.readRefusalText(error)
                        ?? 'Die Neuermittlung konnte nicht freigegeben werden.');
            }
        });
    }

    /**
     * The German refusal sentence out of a `ProvisionCheckRefusedException` response.
     *
     * `CpControllerExceptionAdvice` answers HTTP 400 with `CoreApiResponseDto`, whose message sits
     * in `data`. Reading it is what turns a business refusal into a readable sentence instead of a
     * generic technical message; anything that is not a non-empty string yields null so the caller
     * falls back to its own wording.
     */
    private readRefusalText(error: any): string {
        const data = error?.error?.data;
        return typeof data === 'string' && data.trim().length ? data : null;
    }

    /**
     * A 403 is the um right `cp_agentChange` answering the hard way — the demotion half of the
     * module-wide optimistic pattern ({@link CpFeatureRight}). ONLY a 403 demotes: a 500 or a
     * dropped connection must leave the flag alone, or a transient fault would silently take the
     * action away from a user who holds the right.
     */
    private demoteOnForbidden(error: any): void {
        if (CpFeatureRight.isForbidden(error)) {
            this.canAgentChange = false;
            this.onForbidden.emit();
        }
    }

    /**
     * Announces the changed event row on the channel the generic CRUD uses, so open lists and
     * object pages refresh without extra wiring. Only `cp.ProvisionContract` is reported: no
     * `ProvisionContractAgent` row and no booking changes here — those appear with the next
     * calculation run. `before`/`after` stay null as in the sibling action dialogs, whose
     * subscribers filter on `objectType` alone.
     */
    private informObjectChanged(): void {
        this.mvsObjectService.objectChangeEvent.next(
            ObjectChangeInformation.create(
                'cp.ProvisionContract',
                ObjectChangeInformationActionEnum.updated,
                null,
                null));
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
    }

}
